import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCurrentLocale, LOCALE_OPTIONS, setPreferredLocale, t } from '../i18n';
import './Navbar.css';

const Navbar: React.FC = () => {
  const { creator, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean | string>(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const generateMenuRef = useRef<HTMLDivElement | null>(null);
  const currentLocale = getCurrentLocale();
  const initialLocale = LOCALE_OPTIONS.some(option => option.value === currentLocale) ? currentLocale : 'en-GB';
  const [language, setLanguage] = useState(initialLocale);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (generateMenuRef.current && !generateMenuRef.current.contains(event.target as Node)) {
        setIsGenerateOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGenerateCreators = async () => {
    if (window.confirm(t('navbar.confirmGenerateCreators'))) {
      try {
        setLoading('creators');
        setProgress(0);
        const token = localStorage.getItem('token');
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.x-perienc.es/api';
        
        const response = await fetch(`${API_BASE_URL}/generate/creators-stream`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 404) throw new Error(t('navbar.error.endpoint'));
        if (!response.body) throw new Error(t('navbar.error.noBody'));

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value, { stream: true });
          const messages = chunk.split('\n');
          
          for (const msg of messages) {
            if (msg.startsWith('data: ')) {
              try {
                const data = JSON.parse(msg.replace('data: ', '').trim());
                if (data.progress !== undefined) {
                  setProgress(data.progress);
                }
                if (data.error) {
                  throw new Error(data.error);
                }
              } catch (e: any) {
                if (e.message && typeof e.message === 'string' && !e.message.includes('JSON')) {
                  throw e;
                }
              }
            }
          }
        }
        
        alert(t('navbar.successGenerateCreators'));
        setIsGenerateOpen(false);
      } catch (error: any) {
        alert(error.message || t('navbar.error.generateCreators'));
      } finally {
        setLoading(false);
        setProgress(null);
      }
    }
  };

  const handleGenerateXperiences = async () => {
    if (window.confirm(t('navbar.confirmGenerateXperiences'))) {
      try {
        setLoading('xperiences');
        // Indicador de UI: progressXperiences = 0
        setProgress(-1); // Usaremos -1 para indicar que es de Xperiences (un modo un poco tramposo pero rápido sin cambiar estados) 
        // Mejor añadimos un estado real pero para no editar mucho el render, reciclamos progress.
        // wait, we can just use setProgress(0) and then check in JSX. Let's just create variables if needed.
        // Actually, let's keep it simple:  
        setProgress(0);
        
        const token = localStorage.getItem('token');
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.x-perienc.es/api';
        
        const response = await fetch(`${API_BASE_URL}/generate/xperiences-stream`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 404) throw new Error(t('navbar.error.endpoint'));
        if (!response.body) throw new Error(t('navbar.error.noBody'));

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value, { stream: true });
          const messages = chunk.split('\n');
          
          for (const msg of messages) {
            if (msg.startsWith('data: ')) {
              try {
                const data = JSON.parse(msg.replace('data: ', '').trim());
                if (data.progress !== undefined) {
                  setProgress(data.progress);
                }
                if (data.error) {
                  throw new Error(data.error);
                }
              } catch (e: any) {
                if (e.message && typeof e.message === 'string' && !e.message.includes('JSON')) {
                  throw e;
                }
              }
            }
          }
        }
        
        alert(t('navbar.successGenerateXperiences'));
        setIsGenerateOpen(false);
      } catch (error: any) {
        alert(error.message || t('navbar.error.generateXperiences'));
      } finally {
        setLoading(false);
        setProgress(null);
      }
    }
  };

  const handleLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedLanguage = setPreferredLocale(event.target.value);
    setLanguage(selectedLanguage);
    window.location.reload();
  };

  const generateLabel =
    loading && progress !== null
      ? `${t('navbar.generating')} (${progress}%)`
      : t('navbar.generate');

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-main">
          <Link to="/" className="navbar-brand">
            <img src="/logo.webp" alt="X-periences" className="navbar-logo" />
            <div>
              <div className="navbar-title"><span className="navbar-brand-name">X-periences</span></div>
              <div className="navbar-subtitle">{t('navbar.subtitle')}</div>
            </div>
          </Link>

          {creator && (
            <div className="navbar-menu">
              <Link to="/" className="navbar-link">{t('navbar.dashboard')}</Link>
              {(creator.role === 'manager' || creator.role === 'administrator') && (
                <>
                  <Link to="/admin/xperiences" className="navbar-link">{t('navbar.modXperiences')}</Link>
                </>
              )}
              {creator.role === 'administrator' && (
                <>
                  <Link to="/admin/creators" className="navbar-link">{t('navbar.manageCreators')}</Link>
                </>
              )}
              {(creator.role === 'manager' || creator.role === 'administrator') && (
                <div className="navbar-generate" ref={generateMenuRef}>
                  <button
                    onClick={() => setIsGenerateOpen((prev) => !prev)}
                    className="navbar-link navbar-generate-trigger"
                    aria-haspopup="menu"
                    aria-expanded={isGenerateOpen}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', display: 'flex', alignItems: 'center' }}
                  >
                    {loading && progress !== null && <span className="spinner"></span>}
                    {generateLabel}
                  </button>
                  {isGenerateOpen && (
                    <div className="navbar-generate-menu" role="menu">
                      <button
                        onClick={handleGenerateCreators}
                        disabled={!!loading}
                        className="navbar-generate-item"
                        role="menuitem"
                      >
                        {t('navbar.generateCreators')}
                      </button>
                      <button
                        onClick={handleGenerateXperiences}
                        disabled={!!loading}
                        className="navbar-generate-item"
                        role="menuitem"
                      >
                        {t('navbar.generateXperiences')}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="navbar-language">
                <label htmlFor="navbar-language-select" className="navbar-language-label">
                  {t('navbar.languageLabel')}
                </label>
                <select
                  id="navbar-language-select"
                  value={language}
                  onChange={handleLanguageChange}
                  className="navbar-language-select"
                >
                  {LOCALE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </select>
              </div>

              <Link to="/profile" className="navbar-link">{t('navbar.profile')}</Link>
              <div className="navbar-user">
                <button onClick={handleLogout} className="navbar-logout">
                  {t('navbar.logout')}
                </button>
              </div>
            </div>
          )}
        </div>

        {creator && (
          <div className="creator-info-bar">
            <div className="creator-stats">
              <span title={t('navbar.totalViews')}>👁️ {creator.views || 0}</span>
              <span title={t('navbar.avgRating')}>⭐ {creator.rating ? Number(creator.rating).toFixed(1) : '0.0'}</span>
              <span title={t('navbar.roleTitle')}>👤 {creator.role || t('navbar.roleDefault')}</span>
              <span
                title={t('navbar.statusTitle')}
                className="creator-status-chip"
                style={{
                  backgroundColor: creator.status === 'approved' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                  color: creator.status === 'approved' ? '#4caf50' : '#ff9800'
                }}
              >
                {creator.status || t('navbar.statusPending')}
              </span>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
